import { useEffect, useState } from 'react';

export interface LightboxPhoto {
  src: string;
  caption: string;
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  openIndex: number | null;
  onClose: () => void;
}

export default function PhotoLightbox({ photos, openIndex, onClose }: PhotoLightboxProps) {
  const [index, setIndex] = useState<number>(openIndex ?? 0);

  useEffect(() => {
    if (openIndex !== null) setIndex(openIndex);
  }, [openIndex]);

  useEffect(() => {
    if (openIndex === null) return undefined;

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') {
        setIndex((current) => (current + 1) % photos.length);
      }
      if (event.key === 'ArrowLeft') {
        setIndex((current) => (current - 1 + photos.length) % photos.length);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [openIndex, onClose, photos.length]);

  if (openIndex === null) return null;

  const activePhoto = photos[index];
  if (!activePhoto) return null;

  const total = photos.length;

  return (
    <div
      className="fixed inset-0 z-50 bg-navy-950/95 flex items-center justify-center p-4 md:p-10 animate-fade-in"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative w-full max-w-5xl flex flex-col items-center animate-scale-in"
        onClick={(event) => event.stopPropagation()}
        role="presentation"
      >
        <img
          src={activePhoto.src}
          alt={activePhoto.caption}
          className="max-h-[72vh] w-auto max-w-full object-contain rounded-xl border border-white/10"
        />
        <div className="mt-5 w-full flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs md:text-sm text-cream-200 text-center sm:text-left max-w-xl">
            {activePhoto.caption}
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-cream-200/60 mr-1">
              {index + 1} / {total}
            </span>
            <button
              type="button"
              onClick={() => setIndex((index - 1 + total) % total)}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-300 cursor-pointer"
              aria-label="Previous photo"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <button
              type="button"
              onClick={() => setIndex((index + 1) % total)}
              className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-all duration-300 cursor-pointer"
              aria-label="Next photo"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="absolute -top-12 right-0 md:-right-2 w-10 h-10 rounded-full bg-white/10 hover:bg-gold-400 text-white hover:text-navy-900 flex items-center justify-center transition-all duration-300 cursor-pointer"
          aria-label="Close photo"
        >
          <i className="ri-close-line text-lg"></i>
        </button>
      </div>
    </div>
  );
}